import { HTTPClientInterface, HTTPRequest } from '@shared/core/HTTPClientInterface';
import { buildURL, SystemURL } from "./URLCatalog";

export namespace PresenteeismFailureAttemptsServiceModel {
    export const defaultTriggerDistanceInMeters = 50;

    export interface FailureAttemptLocationDTO {
        latitude: number;
        longitude: number;
    }

    export interface FailureAttemptUserDTO {
        id: string;
        canonicalUserID: string;
        name?: string;
        email?: string;
        imageURL?: string;
        mobilePhone?: string;
        legajo?: string;
    }

    export interface FailureAttemptBuildingStatsDTO {
        closedTickets: number;
        openedTickets: number;
        pendingTickets: number;
        timestampLastPendingTicket: number;
    }

    export interface FailureAttemptBuildingDTO {
        id: string;
        image?: string;
        internalID?: string;
        name: string;
        organizationID: string;
        latitude?: number;
        longitude?: number;
        stats?: FailureAttemptBuildingStatsDTO;
    }

    export interface FailureAttemptFunctionalUnitDTO {
        buildingID: string;
        id: string;
        internalID?: string;
        name: string;
        organizationID: string;
        ownerName?: string;
    }

    export interface FailureAttemptQontactPointDTO {
        buildingID: string;
        functionalUnitID: string;
        id: string;
        organizationID: string;
        triggerDistance?: number;
    }

    export interface FailureAttemptClosestQontactPointDTO {
        distanceInMeters: number;
        maxDistanceInMeters: number;
        buildingName?: string;
        qontactPoint: FailureAttemptQontactPointDTO;
        building?: FailureAttemptBuildingDTO;
        functionalUnit?: FailureAttemptFunctionalUnitDTO;
    }

    export interface SecondFactorFailureAttemptAnalyticsMetadata {
        controlName?: string;
        controlOutcome?: string;
        controlReason?: string;
        attemptedCompletionMethod?: string;
        detectedDevicePlatform?: string;
        detectedDeviceNextAction?: string;
        boundDevicePlatform?: string;
        requestPlatform?: string;
    }

    export interface FailureAttemptMetadata {
        system?: string;
        sessionID?: string;
        challengeID?: string;
        problemType?: string;
        problemDetails?: unknown;
        appMetadata?: Record<string, unknown>;
        rawPermissionState?: Record<string, unknown>;
        organizationID?: string;
        organizationName?: string;
        offenderPhoneNumber?: string;
        deviceInstallationID?: string;
        detectedDevicePlatform?: string;
        detectedDeviceNextAction?: string;
        detectedDeviceUserAgent?: string;
        boundDevicePlatform?: string;
        requestUserAgent?: string;
        source?: string;
        completionMethodAttempted?: string;
        analytics?: SecondFactorFailureAttemptAnalyticsMetadata;
        [key: string]: unknown;
    }

    export interface PresenteeismFailureAttemptDTO {
        firestoreCreationTimestamp?: number;
        buildingID: string;
        functionalUnitID: string;
        ticketID: string;
        requesterID: string;
        organizationID: string;
        location?: FailureAttemptLocationDTO;
        timestamp: number;
        type: string;
        day: string;
        isLocationBypassed?: boolean;
        failureReason: string;
        errorMessage: string;
        metadata?: FailureAttemptMetadata;
        firestoreUpdateTimestamp?: number;
        firestoreRecordId?: string;
        user?: FailureAttemptUserDTO;
        building?: FailureAttemptBuildingDTO;
        functionalUnit?: FailureAttemptFunctionalUnitDTO;
        closestQontactPoint?: FailureAttemptClosestQontactPointDTO;
    }

    export interface FailureAttemptLocation {
        latitude: number;
        longitude: number;
    }

    export interface FailureAttemptUser {
        id: string;
        canonicalUserID: string;
        name?: string;
        email?: string;
        imageURL?: string;
        mobilePhone?: string;
        legajo?: string;
    }

    export interface FailureAttemptBuildingStats {
        closedTickets: number;
        openedTickets: number;
        pendingTickets: number;
        timestampLastPendingTicket: number;
    }

    export interface FailureAttemptBuilding {
        id: string;
        image?: string;
        internalID?: string;
        name: string;
        organizationID: string;
        latitude?: number;
        longitude?: number;
        stats?: FailureAttemptBuildingStats;
    }

    export interface FailureAttemptFunctionalUnit {
        buildingID: string;
        id: string;
        internalID?: string;
        name: string;
        organizationID: string;
        ownerName?: string;
    }

    export interface FailureAttemptQontactPoint {
        buildingID: string;
        functionalUnitID: string;
        id: string;
        organizationID: string;
        triggerDistance?: number;
    }

    export interface FailureAttemptClosestQontactPoint {
        distanceInMeters: number;
        maxDistanceInMeters: number;
        buildingName?: string;
        qontactPoint: FailureAttemptQontactPoint;
        building?: FailureAttemptBuilding;
        functionalUnit?: FailureAttemptFunctionalUnit;
    }

    export interface PresenteeismFailureAttempt {
        firestoreCreationTimestamp?: number;
        buildingID: string;
        functionalUnitID: string;
        ticketID: string;
        requesterID: string;
        organizationID: string;
        location?: FailureAttemptLocation;
        timestamp: number;
        type: string;
        day: string;
        isLocationBypassed?: boolean;
        failureReason: string;
        errorMessage: string;
        metadata?: FailureAttemptMetadata;
        firestoreUpdateTimestamp?: number;
        firestoreRecordId?: string;
        user?: FailureAttemptUser;
        building?: FailureAttemptBuilding;
        functionalUnit?: FailureAttemptFunctionalUnit;
        closestQontactPoint?: FailureAttemptClosestQontactPoint;
    }

    export interface GetParams {
        day: string;
        requesterID?: string;
        maxDistanceInMeters?: number;
        signal?: AbortSignal;
    }
    
    export class Parser {
        static parseList(
            data: PresenteeismFailureAttemptDTO[]
        ): PresenteeismFailureAttempt[] {
            return data.map((item) => this.parse(item));
        }

        static parse(
            item: PresenteeismFailureAttemptDTO
        ): PresenteeismFailureAttempt {
            return {
                firestoreCreationTimestamp: item.firestoreCreationTimestamp,
                buildingID: item.buildingID,
                functionalUnitID: item.functionalUnitID,
                ticketID: item.ticketID,
                requesterID: item.requesterID,
                organizationID: item.organizationID,
                location: item.location ? {
                    latitude: item.location.latitude,
                    longitude: item.location.longitude,
                } : undefined,
                timestamp: item.timestamp,
                type: item.type,
                day: item.day,
                isLocationBypassed: item.isLocationBypassed,
                failureReason: item.failureReason,
                errorMessage: item.errorMessage,
                metadata: item.metadata,
                firestoreUpdateTimestamp: item.firestoreUpdateTimestamp,
                firestoreRecordId: item.firestoreRecordId,
                user: item.user ? { ...item.user } : undefined,
                building: item.building ? {
                    ...item.building,
                    stats: item.building.stats ? { ...item.building.stats } : undefined,
                } : undefined,
                functionalUnit: item.functionalUnit ? { ...item.functionalUnit } : undefined,
                closestQontactPoint: item.closestQontactPoint ? {
                    distanceInMeters: item.closestQontactPoint.distanceInMeters,
                    maxDistanceInMeters: item.closestQontactPoint.maxDistanceInMeters,
                    buildingName: item.closestQontactPoint.buildingName,
                    qontactPoint: {
                        ...item.closestQontactPoint.qontactPoint,
                        triggerDistance: item.closestQontactPoint.qontactPoint?.triggerDistance
                            ?? defaultTriggerDistanceInMeters,
                    },
                    building: item.closestQontactPoint.building ? {
                        ...item.closestQontactPoint.building,
                        stats: item.closestQontactPoint.building.stats
                            ? { ...item.closestQontactPoint.building.stats }
                            : undefined,
                    } : undefined,
                    functionalUnit: item.closestQontactPoint.functionalUnit
                        ? { ...item.closestQontactPoint.functionalUnit }
                        : undefined,
                } : undefined,
            };
        }
    }
}

export class PresenteeismFailureAttemptsService {
    private httpClient: HTTPClientInterface;

    constructor(httpClient: HTTPClientInterface) {
        this.httpClient = httpClient;
    }

    async getForDay(
        orgId: string,
        params: PresenteeismFailureAttemptsServiceModel.GetParams
    ): Promise<PresenteeismFailureAttemptsServiceModel.PresenteeismFailureAttempt[]> {
        const request = new HTTPRequest();
        const searchParams = new URLSearchParams({
            day: params.day,
        });

        if (params.requesterID) {
            searchParams.set("requesterID", params.requesterID);
        }

        if (params.maxDistanceInMeters !== undefined) {
            searchParams.set("maxDistanceInMeters", String(params.maxDistanceInMeters));
        }

        request.url = `${buildURL(SystemURL.Presenteeism.getFailureAttempts, {
            org_id: orgId,
        })}?${searchParams.toString()}`;
        request.method = "GET";
        request.signal = params.signal;

        const response = await this.httpClient.execute(request);
        return PresenteeismFailureAttemptsServiceModel.Parser.parseList(response.result);
    }
}
